/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed product IDs in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store the viewed product handles in local storage */
  static #HANDLES_KEY = 'viewedProductHandles';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  /** @static @constant {number} The maximum number of handles to store, for the recently viewed slider */
  static #MAX_HANDLES = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The product's handle, for lists that load products by URL.
   */
  static addProduct(productId, productHandle) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (productHandle) {
      let viewedHandles = this.getProductHandles();

      viewedHandles = viewedHandles.filter((handle) => handle !== productHandle);
      viewedHandles.unshift(productHandle);
      viewedHandles = viewedHandles.slice(0, this.#MAX_HANDLES);

      localStorage.setItem(this.#HANDLES_KEY, JSON.stringify(viewedHandles));
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#HANDLES_KEY);
  }

  /**
   * Retrieves the list of recently viewed product IDs from local storage.
   * Entries that are not IDs (handles stored by an earlier version of the theme) are skipped,
   * since search looks the products up by ID.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    return this.#read(this.#STORAGE_KEY).filter((id) => /^\d+$/.test(String(id)));
  }

  /**
   * Retrieves the list of recently viewed product handles from local storage.
   * Falls back to handles that an earlier version of the theme stored in the ID list.
   * @returns {string[]} The list of viewed product handles.
   */
  static getProductHandles() {
    const handles = this.#read(this.#HANDLES_KEY);
    if (handles.length > 0) return handles;

    return this.#read(this.#STORAGE_KEY).filter((value) => !/^\d+$/.test(String(value)));
  }

  /**
   * @param {string} key
   * @returns {string[]}
   */
  static #read(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch {
      return [];
    }
  }
}
